import React, { useEffect, useState } from 'react';
import api from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Format date as dd-mm-yyyy
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function CustomerLedgerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ledger, setLedger] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [summary, setSummary] = useState({ totalSales: 0, totalReceived: 0, totalRefunded: 0 });
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    const res = await api.get(`/customers/${id}/ledger`);
    const refundRes = await api.get(`/refunds`); // full refund list
    const refundList = refundRes.data.filter(r => r.CustomerID == id);

    setLedger(res.data.ledger || []);
    setSummary({
      totalSales: res.data.summary?.totalSales || 0,
      totalReceived: res.data.summary?.totalReceived || 0,
      totalRefunded: refundList.reduce((sum, r) => sum + parseFloat(r.RefundAmount || 0), 0)
    });
    setCustomer(res.data.customer || null);
    setRefunds(refundList);
  };

  const opening = Number(customer?.OpeningBalance || 0);
let runningBalance = opening;

const fullLedger = [
  {
    Date: '',
    Type: 'Opening Balance',
    Ref: '',
    Debit: opening,
    Credit: 0,
    Balance: opening,
  },
  ...[
    ...ledger,
    ...refunds.map(r => ({
      Date: r.RefundDate,
      Type: 'Refund',
      Ref: r.Remarks || 'Refund',
      Debit: Number(r.RefundAmount),
      Credit: 0
    }))
  ]
  .sort((a, b) => new Date(a.Date) - new Date(b.Date))
  .map(row => {
    runningBalance += (Number(row.Debit) || 0) - (Number(row.Credit) || 0);
    return { ...row, Balance: runningBalance };
  }),
];

const totalOutstanding = fullLedger[fullLedger.length - 1]?.Balance || 0;

  const handleExportExcel = () => {
    const data = fullLedger.map(row => ({
      Date: row.Type === 'Opening Balance' ? '' : formatDate(row.Date),
      Type: row.Type,
      Reference: row.Ref,
      'Debit (Invoices/Refunds)': Number(row.Debit || 0).toFixed(2),
      'Credit (Receipts)': Number(row.Credit || 0).toFixed(2),
      Balance: Number(row.Balance || 0).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${customer?.Name || 'Customer'}_Ledger.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(15);
    doc.text(`Customer Ledger: ${customer?.Name || ''}`, 14, 16);
    doc.setFontSize(12);
    doc.text(
      `Total Sales: ₹${Number(summary.totalSales).toFixed(2)}   Total Received: ₹${Number(summary.totalReceived).toFixed(2)}   Refunded: ₹${Number(summary.totalRefunded).toFixed(2)}   Outstanding: ₹${totalOutstanding.toFixed(2)}`,
      14,
      24
    );
    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Type', 'Reference', 'Debit', 'Credit', 'Balance']],
      body: fullLedger.map(row => [
        row.Type === 'Opening Balance' ? '' : formatDate(row.Date),
        row.Type,
        row.Ref,
        Number(row.Debit || 0).toFixed(2),
        Number(row.Credit || 0).toFixed(2),
        Number(row.Balance || 0).toFixed(2)
      ]),
    });
    doc.save(`${customer?.Name || 'Customer'}_Ledger.pdf`);
  };

  return (
    <div className="container mt-4">
      <h2>Customer Ledger</h2>
      {customer && (
        <div className="mb-2">
          <strong>{customer.Name}</strong><br />
          GSTIN: {customer.GSTIN || '-'}<br />
          Phone: {customer.Phone || '-'}
        </div>
      )}

      <div className="mb-3">
        <span className="me-4"><strong>Total Sales:</strong> ₹{Number(summary.totalSales).toFixed(2)}</span>
        <span className="me-4"><strong>Total Received:</strong> ₹{Number(summary.totalReceived).toFixed(2)}</span>
        <span className="me-4"><strong>Total Refunded:</strong> ₹{Number(summary.totalRefunded).toFixed(2)}</span>
        <span className="me-4"><strong>Outstanding:</strong>
<span style={{ color: totalOutstanding > 0 ? 'red' : 'green' }}>
  ₹{Math.abs(totalOutstanding) < 0.01 ? '0.00' : totalOutstanding.toFixed(2)}
</span>

        </span>
      </div>

      <div className="mb-3">
        <button className="btn btn-outline-success me-2" onClick={handleExportExcel}>Export to Excel</button>
        <button className="btn btn-outline-primary me-2" onClick={handleExportPDF}>Export to PDF</button>
      </div>

      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Reference</th>
            <th>Debit (Invoices/Refunds)</th>
            <th>Credit (Receipts)</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {fullLedger.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center text-muted">No transactions</td>
            </tr>
          )}
          {fullLedger.map((row, i) => (
            <tr key={i}>
              <td>{row.Type === 'Opening Balance' ? '' : formatDate(row.Date)}</td>
              <td>{row.Type}</td>
              <td>{row.Ref}</td>
              <td>{Number(row.Debit || 0).toFixed(2)}</td>
              <td>{Number(row.Credit || 0).toFixed(2)}</td>
              <td>{Number(row.Balance || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="btn btn-secondary mt-3" onClick={() => navigate('/customers')}>← Back to Customers</button>
    </div>
  );
}
